import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'
import type { MeUser } from '@cmms/types'
import { useCustomerStore } from './customerStore'
import { queryClient } from '../lib/queryClient'

interface AuthState {
  accessToken: string | null
  refreshToken: string | null
  user: MeUser | null
  isHydrated: boolean

  setTokens: (access: string, refresh: string) => void
  setUser: (user: MeUser | null) => void
  clear: () => void
  markHydrated: () => void
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      accessToken: null,
      refreshToken: null,
      user: null,
      isHydrated: false,

      setTokens: (accessToken, refreshToken) => set({ accessToken, refreshToken }),
      setUser: (user) => set({ user }),
      clear: () => {
        // Also drop the customer selection so the next user logging
        // in on the same browser doesn't inherit the previous one.
        useCustomerStore.getState().clear()
        // Wipe every React Query cache so the next account doesn't
        // see the previous user's data flash on screen before its
        // own queries refetch. We use removeQueries({}) instead of
        // clear() because clear() also cancels in-flight requests
        // which can leave components in a stuck pending state.
        queryClient.removeQueries()
        queryClient.cancelQueries()
        set({ accessToken: null, refreshToken: null, user: null })
      },
      markHydrated: () => set({ isHydrated: true }),
    }),
    {
      name: 'cmms-auth',
      storage: createJSONStorage(() => localStorage),
      partialize: (s) => ({
        accessToken: s.accessToken,
        refreshToken: s.refreshToken,
        user: s.user,
      }),
      onRehydrateStorage: () => (state) => state?.markHydrated(),
    },
  ),
)
